import React, { useEffect, useRef, useState } from "react";
import * as translator from "../../../../../utils/translator";
import { MyAccountStoreFilterDimension } from "../types";

interface Props {
  language: string;
  translationPrefix: string;
  dimensions: MyAccountStoreFilterDimension[];
  valuesByDimension: Record<string, string[]>;
  countsByDimension: Record<string, Record<string, number>>;
  selectedByDimension: Record<string, Set<string>>;
  onToggle: (dimensionKey: string, value: string) => void;
  onClearAll: () => void;
}

// Rows shown before the "+ Mehr anzeigen" link (matches the CMS Test Lab
// category page, filter-bar-test-lab.ejs).
const SEE_MORE_THRESHOLD = 6;

// The dimension label is baked as "Sortieren nach: Biomarker" ("Trier par :
// Biomarqueurs" / "Sort by: Biomarkers"). Split it into a muted prefix and a
// black value so the trigger renders two-tone like the design.
const splitLabel = (label: string): { prefix: string; value: string } => {
  const idx = label.indexOf(":");
  if (idx === -1) return { prefix: "", value: label };
  return { prefix: label.slice(0, idx + 1).trim(), value: label.slice(idx + 1).trim() };
};

const MyAccountStoreFilters: React.FC<Props> = ({
  language,
  translationPrefix,
  dimensions,
  valuesByDimension,
  countsByDimension,
  selectedByDimension,
  onToggle,
  onClearAll,
}) => {
  // Multiple panels may be open at once (e.g. Biomarker + Symptome side by side).
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [expandedByDimension, setExpandedByDimension] = useState<Record<string, boolean>>({});
  // Mobile (<sm) collapses the whole bar into a single "Filtern & Sortieren"
  // pill that opens a full-screen drawer with collapsible accordion groups —
  // exactly like the CMS Test Lab category page (filter-bar-test-lab.ejs).
  // Desktop keeps the inline triggers + floating panels untouched.
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const t = (key: string): string => translator.translate(language, "my_account", key);

  const hasActiveFilters = Object.values(selectedByDimension).some((s) => s.size > 0);
  const totalSelected = Object.values(selectedByDimension).reduce((sum, s) => sum + s.size, 0);

  const toggleOpen = (key: string): void => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Mobile drawer open/close — lock background scroll while the full-screen
  // drawer is up (handler-based, mirroring the CMS category page behavior).
  const openDrawer = (): void => {
    setDrawerOpen(true);
    document.body.style.overflow = "hidden";
  };
  const closeDrawer = (): void => {
    setDrawerOpen(false);
    document.body.style.overflow = "";
  };

  const toggleAccordion = (key: string): void => {
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Close every open panel when clicking outside the filter bar. A document
  // listener (rather than a transparent backdrop) is used so it doesn't capture
  // clicks meant for the panels themselves — e.g. "+ Mehr anzeigen".
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenKeys(new Set());
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  // Option list for one dimension. Used only by the mobile combined panel;
  // desktop keeps its own inline copy inside the floating panels below.
  const renderOptions = (key: string): React.ReactNode => {
    const selected = selectedByDimension[key] ?? new Set<string>();
    const values = valuesByDimension[key] ?? [];
    const counts = countsByDimension[key] ?? {};
    const isExpanded = expandedByDimension[key] ?? false;
    const visibleValues = isExpanded ? values : values.slice(0, SEE_MORE_THRESHOLD);
    const hasMore = values.length > SEE_MORE_THRESHOLD;

    if (values.length === 0) {
      return (
        <p className="font-body text-[14px] text-[#99a3b2] m-0">
          {t(`${translationPrefix}/filters/empty`)}
        </p>
      );
    }

    return (
      <>
        <ul className="flex flex-col gap-[16px] m-0 p-0 list-none">
          {visibleValues.map((val) => {
            const isSelected = selected.has(val);
            const valueLabelKey = `${translationPrefix}/filters/${key}/values/${val}`;
            const translated = translator.translate(language, "my_account", valueLabelKey);
            const optionLabel = translated && translated !== valueLabelKey ? translated : val;
            const count = counts[val] ?? 0;
            return (
              <li key={val}>
                <label className="flex items-start gap-[16px] cursor-pointer select-none">
                  <span className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggle(key, val)}
                      className="peer appearance-none w-[24px] h-[24px] rounded-[4px] border border-solid border-[#dbe1eb] bg-white cursor-pointer m-0 checked:bg-primary-button-gradient checked:border-[#15967c] transition-colors"
                    />
                    <svg
                      viewBox="0 0 24 24"
                      className="absolute inset-0 w-[24px] h-[24px] text-white pointer-events-none opacity-0 peer-checked:opacity-100"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M5 12.5l4.5 4.5L19 7"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="font-header font-medium text-[14px] leading-[1.2] text-black mt-[2px]">
                    {optionLabel}
                    <span className="text-[#99a3b2] text-[12px] font-medium"> ({count})</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {hasMore && (
          <button
            type="button"
            className="block mt-[12px] bg-transparent border-0 p-0 font-body text-[14px] text-[#616975] underline cursor-pointer text-left"
            onClick={() =>
              setExpandedByDimension((prev) => ({ ...prev, [key]: !isExpanded }))
            }
          >
            {isExpanded
              ? t(`${translationPrefix}/filters/seeLess`)
              : t(`${translationPrefix}/filters/seeMore`)}
          </button>
        )}
      </>
    );
  };

  return (
    <div className="relative mb-8" ref={containerRef}>
      {/* Desktop (sm+) filter bar — unchanged. */}
      <div className="hidden sm:flex flex-wrap items-center gap-x-[40px] gap-y-4">
        {dimensions.map((dimension) => {
          const key = dimension.dimensionKey;
          const selected = selectedByDimension[key] ?? new Set<string>();
          const selectedCount = selected.size;
          const isOpen = openKeys.has(key);
          const values = valuesByDimension[key] ?? [];
          const counts = countsByDimension[key] ?? {};
          const isExpanded = expandedByDimension[key] ?? false;
          const visibleValues = isExpanded ? values : values.slice(0, SEE_MORE_THRESHOLD);
          const hasMore = values.length > SEE_MORE_THRESHOLD;

          const { prefix, value: valueLabel } = splitLabel(
            translator.translate(language, "my_account", dimension.labelTranslationKey),
          );

          return (
            <div key={key} className="relative">
              {/* Trigger — "Sortieren nach: Biomarker ▾" */}
              <button
                type="button"
                className="flex items-center gap-[16px] cursor-pointer bg-transparent border-0 p-0 m-0 select-none"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => toggleOpen(key)}
              >
                {prefix && (
                  <span className="font-header font-medium text-[18px] leading-[1.5] text-black/50 whitespace-nowrap">
                    {prefix}
                  </span>
                )}
                <span className="flex items-center gap-[7px]">
                  <span className="font-header font-medium text-[18px] leading-[1.5] text-black whitespace-nowrap">
                    {valueLabel}
                    {selectedCount > 0 ? ` (${selectedCount})` : ""}
                  </span>
                  <svg
                    viewBox="0 0 12 12"
                    className={`w-[12px] h-[12px] text-black/50 flex-shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M2 4l4 4 4-4z" />
                  </svg>
                </span>
              </button>

              {/* Floating checkbox panel */}
              {isOpen && (
                <div
                  className="absolute z-[60] left-0 top-full mt-[8px] bg-white border border-solid border-[#dbe1eb] rounded-[20px] w-[275px] py-[20px] px-[20px] max-h-[400px] overflow-y-auto"
                  role="listbox"
                >
                  {values.length === 0 ? (
                    <p className="font-body text-[14px] text-[#99a3b2] m-0">
                      {t(`${translationPrefix}/filters/empty`)}
                    </p>
                  ) : (
                    <>
                      <ul className="flex flex-col gap-[16px] m-0 p-0 list-none">
                        {visibleValues.map((val) => {
                          const isSelected = selected.has(val);
                          const valueLabelKey = `${translationPrefix}/filters/${key}/values/${val}`;
                          const translated = translator.translate(language, "my_account", valueLabelKey);
                          const optionLabel = translated && translated !== valueLabelKey ? translated : val;
                          const count = counts[val] ?? 0;
                          return (
                            <li key={val}>
                              <label className="flex items-start gap-[16px] cursor-pointer select-none">
                                <span className="relative flex-shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => onToggle(key, val)}
                                    className="peer appearance-none w-[24px] h-[24px] rounded-[4px] border border-solid border-[#dbe1eb] bg-white cursor-pointer m-0 checked:bg-primary-button-gradient checked:border-[#15967c] transition-colors"
                                  />
                                  <svg
                                    viewBox="0 0 24 24"
                                    className="absolute inset-0 w-[24px] h-[24px] text-white pointer-events-none opacity-0 peer-checked:opacity-100"
                                    fill="none"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M5 12.5l4.5 4.5L19 7"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                                <span className="font-header font-medium text-[14px] leading-[1.2] text-black mt-[2px]">
                                  {optionLabel}
                                  <span className="text-[#99a3b2] text-[12px] font-medium"> ({count})</span>
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                      {hasMore && (
                        <button
                          type="button"
                          className="block mt-[12px] bg-transparent border-0 p-0 font-body text-[14px] text-[#616975] underline cursor-pointer text-left"
                          onClick={() =>
                            setExpandedByDimension((prev) => ({ ...prev, [key]: !isExpanded }))
                          }
                        >
                          {isExpanded
                            ? t(`${translationPrefix}/filters/seeLess`)
                            : t(`${translationPrefix}/filters/seeMore`)}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {hasActiveFilters && (
          <button
            type="button"
            className="bg-transparent border-0 p-0 font-body text-[14px] text-[#616975] underline cursor-pointer"
            onClick={onClearAll}
          >
            {t(`${translationPrefix}/filters/clearAll`)}
          </button>
        )}
      </div>

      {/* Mobile (<sm): a single "Filtern & Sortieren" trigger pill that opens a
          full-screen drawer with collapsible accordion groups — mirrors the CMS
          Test Lab category page (filter-bar-test-lab.ejs). */}
      <div className="sm:hidden">
        <button
          type="button"
          className="flex items-center gap-[8px] bg-white border border-solid border-[#dbe1eb] rounded-[12px] h-[48px] px-[13px] w-full appearance-none cursor-pointer"
          onClick={openDrawer}
        >
          <svg viewBox="0 0 24 24" className="w-[24px] h-[24px] flex-shrink-0 text-black" fill="none" aria-hidden="true">
            <path d="M3 7h18M6 12h12M9 17h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span className="font-header font-medium text-[14px] text-black">
            {t(`${translationPrefix}/filters/filterAndSort`)} ({totalSelected})
          </span>
          <svg viewBox="0 0 16 16" className="w-[16px] h-[16px] ml-auto flex-shrink-0 text-black" fill="none" aria-hidden="true">
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Mobile drawer (full-screen) */}
      {drawerOpen && (
        <div
          className="sm:hidden fixed inset-0 z-[9998] bg-white flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-[20px] pt-[27px] pb-[16px]">
            <h2 className="font-header font-semibold text-[24px] leading-[1.5] text-black m-0">
              {t(`${translationPrefix}/filters/filterAndSort`)}
            </h2>
            <button
              type="button"
              className="p-[4px] bg-transparent border-0 cursor-pointer text-black"
              onClick={closeDrawer}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" className="w-[24px] h-[24px]" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Accordion groups */}
          <div className="flex-1 overflow-y-auto px-[20px]">
            <div className="border border-solid border-[#dbe1eb] rounded-[16px] bg-white overflow-hidden">
              {dimensions.map((dimension, idx) => {
                const key = dimension.dimensionKey;
                const { value: valueLabel } = splitLabel(
                  translator.translate(language, "my_account", dimension.labelTranslationKey),
                );
                const selectedCount = (selectedByDimension[key] ?? new Set<string>()).size;
                const isAccordionOpen = openAccordions.has(key);
                return (
                  <React.Fragment key={key}>
                    {idx > 0 && <div className="h-px bg-[#dbe1eb] mx-[20px]" />}
                    <div>
                      <button
                        type="button"
                        className="flex items-center justify-between w-full px-[20px] py-[16px] bg-transparent border-0 cursor-pointer"
                        aria-expanded={isAccordionOpen}
                        onClick={() => toggleAccordion(key)}
                      >
                        <span className="font-header font-semibold text-[16px] text-black">
                          {valueLabel}
                          {selectedCount > 0 ? ` (${selectedCount})` : ""}
                        </span>
                        <svg
                          viewBox="0 0 12 8"
                          className={`w-[12px] h-[8px] text-black transition-transform ${
                            isAccordionOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          aria-hidden="true"
                        >
                          <path d="M1 1.5l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {isAccordionOpen && (
                        <div className="px-[20px] pb-[16px]">{renderOptions(key)}</div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Bottom actions */}
          <div className="px-[20px] pt-[16px] pb-[24px] flex flex-col items-center gap-[12px]">
            <button
              type="button"
              className="w-full h-[48px] rounded-full font-header font-semibold text-[16px] text-black bg-white border border-solid border-black cursor-pointer"
              onClick={closeDrawer}
            >
              {t(`${translationPrefix}/filters/apply`)}
            </button>
            <button
              type="button"
              className="font-header font-medium text-[14px] text-black underline decoration-solid bg-transparent border-0 cursor-pointer p-0"
              onClick={onClearAll}
            >
              {t(`${translationPrefix}/filters/clearAll`)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccountStoreFilters;
